/**
 * Stub <PermissionGate> — meant to render children only when the
 * current user holds every permission in `requires`.
 *
 * There is no client-side permission signal to evaluate `requires`
 * against yet: `GET /me`'s `capabilities` block is hardcoded to `null`
 * server-side (`MeResponse.java`) until the v1.1 policy evaluator
 * lands — `RolePolicyRule` / `RolePolicyRuleTargetRole` have a schema
 * (V0010) but no repository and no runtime wiring
 * (docs/design/11-authz.md §Migration + ops). So this component keeps
 * `requires` (and `fallback`) in its prop type — call sites should
 * keep declaring what they *intend* to gate on — but deliberately does
 * not read either. Renaming `requires` away to `_requires` to silence
 * an unused-var lint would hide that gap instead of documenting it, so
 * we just don't destructure it.
 *
 * Default behaviour is fully open: always render `children`. Never
 * render `fallback` — showing a "denied" state we cannot actually
 * justify from real permission data is a worse bug than an occasional
 * backend 403. The backend remains authoritative regardless.
 *
 * DevPanel's `permissionBypass` toggle (see ./bypassContext) exists to
 * force every gate open once real evaluation lands; today it changes
 * nothing because the gate is already unconditionally open.
 *
 * TODO(BE): swap the body for a real `usePermissions()` check once
 * `GET /me` returns a populated `capabilities` block.
 */
import type { ReactNode } from 'react'

interface PermissionGateProps {
  readonly requires: ReadonlyArray<string>
  readonly children: ReactNode
  readonly fallback?: ReactNode
}

export function PermissionGate({ children }: PermissionGateProps) {
  return <>{children}</>
}
